import { Game } from "@aura-frontend/omnibus-az-client";

import {
  DEFAULT_AWAY_TEAM_COLOR,
  DEFAULT_HOME_TEAM_COLOR,
  DEFAULT_TEAM_COLOR,
  TEAM_COLORS,
} from "@/lib/constants";
import { formatHHMM } from "@/utils/dateUtils";
import { getEventTimes } from "@/utils/eventUtils";

import {
  GameType,
  isPractice,
  type OverviewFormValues,
} from "../EventForm.schema";

// GameType -> label map. Used by OverviewLive.tsx's read-only summary, and as the
// source for the practice-subtype select below.
export const EVENT_TYPE_OPTIONS: { label: string; value: GameType }[] = [
  { label: "Practice", value: "batting_practice" },
  { label: "Bullpen", value: "bullpen" },
  { label: "Game", value: "game" },
];

// UI-only category for the top-level "Type" select in the editable Overview form.
export const EVENT_CATEGORY_OPTIONS: {
  label: string;
  value: "practice" | "game";
}[] = [
  { label: "Practice", value: "practice" },
  { label: "Game", value: "game" },
];

// Practice subtype options for the new "Practice Type" select.
export const PRACTICE_SUBTYPE_OPTIONS: {
  label: string;
  value: "batting_practice" | "bullpen";
}[] = [
  { label: "Batting Practice", value: "batting_practice" },
  { label: "Bullpen", value: "bullpen" },
];

type TeamColor = (typeof TEAM_COLORS)[number];

// Omnibus returns saved colors as { homeColor, awayColor } (its serialization
// alias), though the spec types them as { home, away }; either is read
type SavedTeamColors = {
  home?: string | null;
  away?: string | null;
  homeColor?: string | null;
  awayColor?: string | null;
};

/**
 * A saved "#RRGGBB" color as a picker option, or `fallback` when it isn't one
 * of TEAM_COLORS (e.g. set by another client).
 */
export const toTeamColor = (
  saved: string | null | undefined,
  fallback: TeamColor,
): TeamColor => {
  const hex = saved?.replace(/^#/, "").toUpperCase();
  return TEAM_COLORS.find((color) => color === hex) ?? fallback;
};

const getSavedTeamColors = (event: Game) => {
  const colors = event.teamColors as SavedTeamColors | null | undefined;
  return {
    home: colors?.homeColor ?? colors?.home,
    away: colors?.awayColor ?? colors?.away,
  };
};

export function buildEventFormDefaultValues(
  event: Game | undefined,
  fieldId: string,
  scheduleName?: string | null,
  scheduleNotes?: string | null,
): OverviewFormValues {
  if (!event) {
    return {
      field: fieldId,
      type: "game",
      name: scheduleName ?? "",
      homeTeam: "",
      awayTeam: "",
      homeTeamColor: DEFAULT_HOME_TEAM_COLOR,
      awayTeamColor: DEFAULT_AWAY_TEAM_COLOR,
      startDate: undefined as unknown as Date,
      startTime: "",
      endTime: "",
      additionalNotes: scheduleNotes ?? "",
    } as unknown as OverviewFormValues;
  }

  const { startTime, endTime } = getEventTimes(event);
  const savedColors = getSavedTeamColors(event);

  const base = {
    field: fieldId,
    name: scheduleName ?? "",
    startDate: new Date(startTime),
    startTime: formatHHMM(startTime),
    endTime: endTime ? formatHHMM(endTime) : "",
    additionalNotes: scheduleNotes ?? "",
  };

  if (event.type && isPractice(event.type)) {
    const homeUuid = event.teams?.home ?? "";
    const legacyDisplay = event.generated_display_name ?? "";
    return {
      ...base,
      type: event.type,
      team: homeUuid,
      // generated_display_name falls back to "Home x Away" for legacy practices
      // where teams.home wasn't persisted — ignore in that case.
      teamName: homeUuid ? legacyDisplay : "",
      teamColor: toTeamColor(savedColors.home, DEFAULT_TEAM_COLOR),
    };
  }

  return {
    ...base,
    type: "game",
    homeTeam: event.teams?.home ?? "",
    awayTeam: event.teams?.away ?? "",
    homeTeamColor: toTeamColor(savedColors.home, DEFAULT_HOME_TEAM_COLOR),
    awayTeamColor: toTeamColor(savedColors.away, DEFAULT_AWAY_TEAM_COLOR),
  };
}
